
"use client";

import { createContext, useContext } from "react";
import { 
    getAuth, 
    signInWithPopup, 
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    Auth,
    User as FirebaseUser
} from "firebase/auth";
import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { firebaseClientConfig } from '@/lib/firebase-client-config';
import type { UserProfile } from '@/types';
import { useCollection } from './firestore/use-collection';
import { useDoc } from './firestore/use-doc';

// This interface defines the shape of our context.
export interface FirebaseContextType {
    app: FirebaseApp | null;
    auth: Auth | null;
    firestore: Firestore | null;
    storage: FirebaseStorage | null;
    user: UserProfile | null;
    isUserLoading: boolean;
    logout: () => void;
}

// Create the context with a default undefined value.
export const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

/**
 * Custom hook to use the Firebase context.
 * This is the primary way components will access Firebase services and user state.
 */
export function useFirebase() {
    const context = useContext(FirebaseContext);
    if (context === undefined) {
        throw new Error("useFirebase must be used within a FirebaseClientProvider");
    }
    return context;
}

// Convenience hooks to access specific firebase services more easily.
export function useAuthContext() {
    const { auth } = useFirebase();
    if (!auth) throw new Error("Auth not initialized, make sure you are inside a FirebaseClientProvider");
    return auth;
}

export function useFirestore() {
    const { firestore } = useFirebase();
    if (!firestore) throw new Error("Firestore not initialized, make sure you are inside a FirebaseClientProvider");
    return firestore;
}

export function useStorage() {
    const { storage } = useFirebase();
    if (!storage) throw new Error("Storage not initialized, make sure you are inside a FirebaseClientProvider");
    return storage;
}

/**
 * Handles the Google sign-in process using a popup.
 * It now requires the auth object from the context to ensure the same instance is used.
 */
export const signInWithGoogle = async (auth: Auth): Promise<FirebaseUser> => {
    const provider = new GoogleAuthProvider();

    try {
        console.log("signInWithGoogle: Calling signInWithPopup...");
        const result = await signInWithPopup(auth, provider);
        console.log("signInWithGoogle: Success!", result.user);
        return result.user;
    } catch (error) {
        console.error("signInWithGoogle: Failed.", error);
        // Re-throw the error to be caught by the calling component
        throw error;
    }
};

/**
 * Handles the sign-in process for the "Cook" role.
 * It now requires the auth object from the context and the password from the form.
 */
export const signInAsCook = async (auth: Auth, password: string): Promise<FirebaseUser> => {
    // IMPORTANT: You must create a user in your Firebase project with this email.
    const email = "cocina@barcade.com";

    try {
        console.log("signInAsCook: Calling signInWithEmailAndPassword...");
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log("signInAsCook: Success!", userCredential.user);
        return userCredential.user;
    } catch (error) {
        console.error("signInAsCook: Failed.", error);
        // Re-throw the error to be caught by the calling component
        throw error;
    }
}

export { useCollection, useDoc };
